import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { QuestionGroup, Questionnaire } from "./models";
import { QuestionnaireSubtaskView } from "./QuestionnaireSubtaskView";
import { Typography } from "@mui/material";
import BorderLinearProgress from "../../../BorderLinearProgress";
import { useStorage } from "../../../../services/StorageProvider";
import { useApiService } from "../../../../services/ApiServiceProvider";
import SnackbarAlert, { Severity } from "../../../alert/SnackbarAlert";
import { translateReactNode } from "../../../../utils/translateReactNode";
import { i18nLanguageToLabel } from "../../../utils";

export const QuestionnaireTaskView = (
  {questionnaire, onChange, onCompletion} :
  {
    questionnaire : Questionnaire,
    onChange:(questionnaire: Questionnaire) => void,
    onCompletion: (questionnaire: Questionnaire) => void
  }) => {
  const [groupIndex, setGroupIndex] = React.useState(0);
  const { title, description, content, groups } = questionnaire;
  const apiService = useApiService();
  const { storage } = useStorage();
  const session: any = storage.session;

  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [snackbarContent, setSnackbarContent] = useState<ReactNode>();
  const [snackbarSeverity, setSnackbarSeverity] = useState<Severity>('info');
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (title: string, content: ReactNode, severity: Severity) => {
    setSnackbarTitle(title);
    setSnackbarContent(content);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
    // Close snackbar after 5 seconds
    setTimeout(() => {
      handleSnackbarClose();
    }, 5000);
  };

  const isRequired = (group: QuestionGroup) => {
    return group.condition
      ? group.condition(questionnaire)
      : true;
  };

  const handleOnCompletion = async (group: QuestionGroup) => {
    // console.log('Group completed', group);
    groups[groupIndex] = group;

    let newGroupIndex = groupIndex + 1;
    if (newGroupIndex < groups.length) {
      const groupsRemaining = groups.slice(newGroupIndex)
      for (const remainingGroup of groupsRemaining) {
        if (isRequired(remainingGroup)) {
          newGroupIndex = groups.indexOf(remainingGroup);
          break;
        } else {
          newGroupIndex++;
        }
      }
    }
    console.log('newGroupIndex', newGroupIndex);

    if (newGroupIndex === groups.length) {
      setSubmitting(true);
      try {
        console.log('session', session);
        console.log('questionnaire....', questionnaire.startedAt);
        const payload = createPayload(questionnaire);
        const startAt = questionnaire.startedAt || new Date().toISOString();
        const completedAt = questionnaire.completedAt || new Date().toISOString();
        payload.sessionId = session.id;
        payload.subjectId = session.subjectId;
        payload.startedAt = startAt;
        payload.completedAt = completedAt;
        payload.language = i18nLanguageToLabel(i18n.language);

        console.log('Payload', payload)

        const _response = await apiService.createQuestionnaire(`/api/${questionnaire.id}`, payload);

        onCompletion(questionnaire);
      } catch (error: Error | any) {
        // console.error('Network error', 'Check your internet connection and try again', error);
        showSnackbar(
          `Could not create ${questionnaire.title}`,
          error?.message || 'Network error: Check your internet connection and try again',
          'error'
        );
      }
      setSubmitting(false);
    } else {
      setGroupIndex(newGroupIndex);
    }
  };

  const handleOnChange = (group: QuestionGroup) => {
    const newQuestionnaire = {...questionnaire};
    newQuestionnaire.groups[groupIndex] = group;
    onChange(newQuestionnaire);
  };

  const group = groupIndex < groups.length ? groups[groupIndex] : null;
  const { t, i18n } = useTranslation(questionnaire.cohort);

  return (
    <div>
      {groups.length >1 &&
        <BorderLinearProgress
          variant="determinate"
          value={(groupIndex + 1) / (groups.length + 1) * 100}
        />
      }
      <div style={{ marginBottom: '1rem' }}>
        <Typography variant='h3' component='h1' id='page-heading' tabIndex={-1} sx={{ mb: 2 }}>{t(title)}</Typography>
        {description && <Typography variant='body1'>{t(description)}</Typography>}
      </div>
        {translateReactNode(content, questionnaire.cohort)}
      {group && (
        <QuestionnaireSubtaskView
          group={group}
          questionnaire={questionnaire}
          submitting={submitting}
          onChange={handleOnChange}
          onCompletion={handleOnCompletion}
        />
      )}
      <SnackbarAlert
        open={snackbarOpen}
        title={snackbarTitle}
        severity={snackbarSeverity}
        content={snackbarContent}
        vertical='top'
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

function createPayload(questionnaire: Questionnaire): { [key: string]: any } {
  const payload: { [key: string]: any } = {};
  questionnaire.groups.forEach((group) => {
    group.questions.forEach((question) => {
      payload[question.key] = question.value;

      if (question.options) {
        question.options.forEach((option) => {
        if (option && option.followUpQuestions) {
          option.followUpQuestions.forEach((followUpQuestion) => {
            if (payload[followUpQuestion.key]) { console.error('Payload already contains this key', followUpQuestion.key); }
            payload[followUpQuestion.key] = followUpQuestion.value;
          });
        }
        });
      }
    });
  });

  return payload;
}
